/**
 * AuthContext — session state + useAuth() hook.
 *
 * Wraps the Supabase Auth session so any component under <AuthProvider>
 * can read session, loading state, signOut, and trigger OTP login.
 *
 * First-login upsert: on every auth state change, if the signed-in auth
 * user has no linked Contact (userId IS NULL), we look up by email and
 * link them. Orchist's "Orchists ARE Contacts" tethering stays intact.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

interface ContactRow {
  id: string;
  email: string | null;
  userId: string | null;
  isOrchist: boolean;
  invitationSentAt: string | null;
  invitationSentBy: string | null;
  invitationProposedAcceptedAt: string | null;
  /** The canonical "I knew" date — when this Orchist actually remembered. */
  invitationAcceptedAt: string | null;
  /** When the recognition was confirmed (= acknowledgment moment, often === invitationAcceptedAt). */
  invitationConfirmedAt: string | null;
  publicIdentityString: string | null;
  publicIdentityMode: 'NAMED' | 'ALIASED' | 'GENERIC' | null;
  /** Stored name (kept across mode switches; visible publicly only when mode=NAMED) */
  namedIdentity: string | null;
  /** Stored alias (kept across mode switches; visible publicly only when mode=ALIASED) */
  aliasedIdentity: string | null;
  glyphSeed: string | null;
  timezone: string | null;
  /**
   * Free-Will Doctrine: when set, the Orchist has set down the recognition.
   * The record stays. The Line preserves the trail.
   * Re-acknowledgment clears this back to null.
   */
  orchistDisownedAt: string | null;
  orchistDisownedReason: string | null;
  /** Phase G.7.10: per-row anonymity flag (set at invite by inviter) */
  invitationAnonymous: boolean;
  /** Phase G.7.10: this Orchist's default anonymity for invitations they make */
  defaultInviteAnonymous: boolean;
}

const CONTACT_COLUMNS =
  'id, email, userId, isOrchist, invitationSentAt, invitationSentBy, invitationProposedAcceptedAt, invitationAcceptedAt, invitationConfirmedAt, publicIdentityString, publicIdentityMode, namedIdentity, aliasedIdentity, glyphSeed, timezone, orchistDisownedAt, orchistDisownedReason, invitationAnonymous, defaultInviteAnonymous';

interface AuthState {
  /** true until Supabase has restored/fetched the initial session */
  loading: boolean;
  session: Session | null;
  user: User | null;
  /** The Contact row linked to this auth user (null if no match or still loading) */
  contact: ContactRow | null;
  /** true if contact lookup has completed (even if it returned null) */
  contactChecked: boolean;
}

interface AuthContextValue extends AuthState {
  /** Send OTP magic link to this email. Returns error (if any). */
  signInWithOtp: (email: string) => Promise<{ error: Error | null }>;
  /** Sign the current session out. */
  signOut: () => Promise<void>;
  /**
   * Re-fetch the linked Contact row for the current session. Useful after
   * a flow that mutates Contact server-side (e.g. acknowledge_orchist)
   * so the UI reflects the new state without requiring a full reload.
   */
  refreshContact: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    loading: true,
    session: null,
    user: null,
    contact: null,
    contactChecked: false,
  });

  // Restore session on mount + listen for auth state changes
  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      applySession(data.session);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (cancelled) return;

      // The Anchor Swap: when Supabase Auth confirms an email change
      // (USER_UPDATED with a new email), sync the change into our
      // core.Contact + core.ContactEmail surface. The RPC is idempotent —
      // safe to call when no actual change happened (e.g., on refresh).
      if (event === 'USER_UPDATED' && session?.user?.email) {
        try {
          await supabase.schema('core').rpc('sync_email_after_auth_change');
        } catch (err) {
          // Non-fatal — on next session refresh the sync will retry.
          // Log to console for visibility during dev.
          // eslint-disable-next-line no-console
          console.warn('Anchor swap sync failed:', err);
        }
      }

      applySession(session);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Update state from a Supabase session, then look up/link the Contact row. */
  async function applySession(session: Session | null) {
    setState((s) => ({ ...s, loading: false, session, user: session?.user ?? null }));

    if (!session?.user) {
      setState((s) => ({ ...s, contact: null, contactChecked: true }));
      return;
    }

    const user = session.user;
    const email = user.email ?? null;

    // Try to find Contact already linked by userId
    const { data: linkedContact } = await supabase
      .schema('core')
      .from('Contact')
      .select(CONTACT_COLUMNS)
      .eq('userId', user.id)
      .maybeSingle();

    if (linkedContact) {
      setState((s) => ({ ...s, contact: linkedContact as ContactRow, contactChecked: true }));
      return;
    }

    // No userId link yet — try email match (first-login upsert)
    if (email) {
      const { data: byEmail } = await supabase
        .schema('core')
        .from('Contact')
        .select(CONTACT_COLUMNS)
        .eq('email', email)
        .is('userId', null)
        .maybeSingle();

      if (byEmail) {
        // Link this Contact to the auth user (userId upsert)
        const { data: updated, error: linkError } = await supabase
          .schema('core')
          .from('Contact')
          .update({ userId: user.id })
          .eq('id', (byEmail as ContactRow).id)
          .select(CONTACT_COLUMNS)
          .maybeSingle();

        if (!linkError && updated) {
          setState((s) => ({ ...s, contact: updated as ContactRow, contactChecked: true }));
          return;
        }
      }
    }

    // No match — user is authenticated but has no Contact yet.
    // This is valid for future invitees whose Contact is created by the
    // inviter flow (Phase G). For now, surface null + contactChecked=true.
    setState((s) => ({ ...s, contact: null, contactChecked: true }));
  }

  async function signInWithOtp(email: string): Promise<{ error: Error | null }> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { error: error ?? null };
  }

  async function signOut(): Promise<void> {
    await supabase.auth.signOut();
    setState({
      loading: false,
      session: null,
      user: null,
      contact: null,
      contactChecked: true,
    });
  }

  /** Re-run the Contact lookup against the current session. */
  async function refreshContact(): Promise<void> {
    const { data } = await supabase.auth.getSession();
    setState((s) => ({ ...s, contactChecked: false }));
    await applySession(data.session);
  }

  return (
    <AuthContext.Provider value={{ ...state, signInWithOtp, signOut, refreshContact }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
